import { Building2Icon, LockIcon, type LucideIcon } from 'lucide-react';
import { z } from 'zod';

import type { AdminWorkspaceView } from '@/lib/api/generated/model';
// The module, not the barrel: this file is read by the console's route schema.
import { VISIBILITY_NAME } from '@/features/workspaces/accessCopy';

/**
 * Admin → Workspaces (spec 191, service spec 157): every workspace, its owners,
 * members and last activity, and those without an active owner.
 */

export const WORKSPACE_FILTERS = ['all', 'without_owner'] as const;
export type WorkspaceFilter = (typeof WORKSPACE_FILTERS)[number];

const WORKSPACE_SORTS = [
  'name.asc',
  'name.desc',
  'activity.desc',
  'activity.asc',
  'members.desc',
  'members.asc',
] as const;
export type WorkspaceSortParam = (typeof WORKSPACE_SORTS)[number];
export type WorkspaceSortKey = 'name' | 'activity' | 'members';

export const WORKSPACES_TABS = ['workspaces', 'activity'] as const;
export type WorkspacesTab = (typeof WORKSPACES_TABS)[number];

/** A visibility's line icon: a lock for Private, a building for Organization. */
export const VISIBILITY_ICON: Record<'private' | 'organization', LucideIcon> = {
  private: LockIcon,
  organization: Building2Icon,
};

export const VISIBILITY_FILTERS = ['any', 'private', 'organization'] as const;
export type VisibilityFilter = (typeof VISIBILITY_FILTERS)[number];

/** A day as the activity's range names it, `YYYY-MM-DD` (UTC). */
const DAY = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

/**
 * The view, in the address: a search, the slice and the column order, so a
 * notification can open the page on "Without an active owner" and a filtered
 * view can be shared. `q` is coerced: the router reads `?q=10` as a number.
 *
 * Spec 195 adds the tab, the visibility chips, the workspace whose sheet is
 * open, and the activity's range and workspace, so a link to "Payments' access
 * over the last 7 days" opens the same view for another admin (SC-003).
 */
export const workspacesSearchSchema = z.object({
  q: z
    .union([z.string(), z.number(), z.boolean()])
    .transform(String)
    .optional()
    .catch(undefined),
  show: z.enum(WORKSPACE_FILTERS).optional().catch(undefined),
  sort: z.enum(WORKSPACE_SORTS).optional().catch(undefined),
  tab: z.enum(WORKSPACES_TABS).optional().catch(undefined),
  visibility: z.enum(['private', 'organization']).optional().catch(undefined),
  workspace: z.string().min(1).optional().catch(undefined),
  from: DAY,
  to: DAY,
  logWorkspace: z.string().min(1).optional().catch(undefined),
});

export type WorkspacesSearch = z.infer<typeof workspacesSearchSchema>;

/**
 * The order a slice opens in when the address names none (spec 195 FR-002):
 * the busiest first, so the list starts with what is in use. The slice without
 * an active owner opens quietest first instead — the list does not say when an
 * owner was deactivated, and the longest-quiet workspace is the likeliest to
 * have been left behind.
 */
export function defaultWorkspaceSort(
  show: WorkspaceFilter,
): WorkspaceSortParam {
  return show === 'without_owner' ? 'activity.asc' : 'activity.desc';
}

export function parseWorkspaceSort(
  param: WorkspaceSortParam | undefined,
  show: WorkspaceFilter = 'all',
): {
  key: WorkspaceSortKey;
  dir: 'asc' | 'desc';
} {
  const [key, dir] = (param ?? defaultWorkspaceSort(show)).split('.') as [
    WorkspaceSortKey,
    'asc' | 'desc',
  ];
  return { key, dir };
}

/** What a header click asks for: the other way round, or the column's natural order. */
export function nextWorkspaceSort(
  current: WorkspaceSortParam | undefined,
  key: WorkspaceSortKey,
  show: WorkspaceFilter = 'all',
): WorkspaceSortParam {
  const { key: now, dir } = parseWorkspaceSort(current, show);
  if (now === key) return `${key}.${dir === 'asc' ? 'desc' : 'asc'}`;
  return key === 'name' ? 'name.asc' : `${key}.desc`;
}

export function matchesWorkspace(
  item: AdminWorkspaceView,
  q: string,
  show: WorkspaceFilter,
  visibility: VisibilityFilter = 'any',
): boolean {
  if (show === 'without_owner' && item.has_active_owner) return false;
  if (visibility !== 'any' && item.visibility !== visibility) return false;
  const needle = q.trim().toLocaleLowerCase();
  if (!needle) return true;
  return (
    item.name.toLocaleLowerCase().includes(needle) ||
    item.owners.some(
      (owner) =>
        owner.user.display_name.toLocaleLowerCase().includes(needle) ||
        owner.user.email.toLocaleLowerCase().includes(needle),
    )
  );
}

export function sortWorkspaces(
  items: AdminWorkspaceView[],
  param: WorkspaceSortParam | undefined,
  show: WorkspaceFilter = 'all',
): AdminWorkspaceView[] {
  const { key, dir } = parseWorkspaceSort(param, show);
  const sign = dir === 'asc' ? 1 : -1;
  const byName = (a: AdminWorkspaceView, b: AdminWorkspaceView) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  return [...items].sort((a, b) => {
    const primary =
      key === 'name'
        ? byName(a, b)
        : key === 'members'
          ? a.member_count - b.member_count
          : Date.parse(a.last_activity_at) - Date.parse(b.last_activity_at);
    return primary !== 0 ? sign * primary : byName(a, b);
  });
}

/** The activity's preset ranges, in days, today included (spec 195 US4). */
export const LOG_PRESETS = [7, 30, 90] as const;
export type LogPreset = (typeof LOG_PRESETS)[number];

/** The last `days` days, UTC, today included. */
export function presetRange(
  days: number,
  today: Date,
): { from: string; to: string } {
  const to = today.toISOString().slice(0, 10);
  const start = new Date(today);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return { from: start.toISOString().slice(0, 10), to };
}

/** The activity range the page opens on: the last 30 days, UTC, today included. */
export function defaultLogRange(today: Date): { from: string; to: string } {
  return presetRange(30, today);
}

/**
 * Which preset a range is, if any: one that ends today and spans exactly its
 * days. Anything else — an older range from a shared link included — is
 * Custom, so a link never claims "Last 7 days" for days that have moved on.
 */
export function presetOf(
  range: { from: string; to: string },
  today: Date,
): LogPreset | 'custom' {
  for (const days of LOG_PRESETS) {
    const preset = presetRange(days, today);
    if (preset.from === range.from && preset.to === range.to) return days;
  }
  return 'custom';
}

export const WORKSPACES_COPY = {
  title: 'Workspaces',
  description:
    'Every workspace in the organization: who owns it, who is in it, and what has happened to its access.',
  figureWords: (n: number) => (n === 1 ? 'workspace' : 'workspaces'),
  withoutOwner: (n: number) =>
    n === 1
      ? '1 has no active owner. Open it and make someone its owner.'
      : `${n} have no active owner. Open each and make someone its owner.`,
  allOwned: 'Every workspace has an active owner.',
  tabsLabel: 'Workspaces console',
  tabs: {
    workspaces: 'Workspaces',
    activity: 'Activity',
  } satisfies Record<WorkspacesTab, string>,
  list: 'All workspaces',
  search: 'Search workspaces or owners',
  filtersLabel: 'Show',
  filters: {
    all: 'All',
    without_owner: 'Without an active owner',
  } satisfies Record<WorkspaceFilter, string>,
  visibilityLabel: 'Visibility',
  // The one name each visibility has everywhere (spec 196 US4).
  visibilityFilters: {
    any: 'Any',
    ...VISIBILITY_NAME,
  } satisfies Record<VisibilityFilter, string>,
  columns: {
    name: 'Workspace',
    visibility: 'Visibility',
    owners: 'Owners',
    members: 'Members',
    activity: 'Last activity',
  },
  visibility: VISIBILITY_NAME,
  deactivated: 'Deactivated',
  deactivatedOwner: (name: string) => `${name} (deactivated)`,
  noActiveOwner: 'No active owner',
  noneMatch: 'No workspace matches.',
  clear: 'Clear search and filters',
  none: 'No workspaces yet.',
  failed: 'The workspaces could not be read just now.',
  exportWorkspaces: 'Export workspaces',
  exportActivity: 'Export activity',
  // The export takes a range only, so a narrowed view says what the file holds.
  exportActivityAll: 'Export activity (all workspaces)',
  activity: 'Access activity',
  activityHint:
    'Members added, removed or leaving, roles, visibility, names, requests to join, and what organization admins did in workspaces they are not in.',
  rangeLabel: 'Range',
  presets: {
    7: 'Last 7 days',
    30: 'Last 30 days',
    90: 'Last 90 days',
    custom: 'Custom',
  } satisfies Record<LogPreset | 'custom', string>,
  from: 'From',
  to: 'To',
  workspaceFilter: 'Workspace',
  allWorkspaces: 'All workspaces',
  onlyWorkspace: (name: string) => `Only ${name}`,
  logEmpty: 'Nothing recorded in these days.',
  logLoading: 'Reading the activity…',
  logFailed: 'The activity could not be read just now.',
  rangeInvalid: 'Choose a range that starts before it ends, a year at most.',
  downloadFailed: 'The file could not be downloaded. Try again in a moment.',
  sheet: {
    created: 'Created',
    lastActivity: 'Last activity',
    ownerless: 'This workspace has no active owner.',
    ownerlessAction: 'Open its Settings to add an owner',
    recorded:
      'It is Private: opening it here is recorded in its activity, once a day.',
    people: 'Owners and members',
    searchPeople: 'Search people',
    owner: 'Owner',
    member: 'Member',
    noPeople: 'No one matches.',
    peopleFailed: 'Its people could not be read just now.',
    recent: 'Recent access',
    noEntries: 'Nothing recorded yet.',
    logFailed: 'Its activity could not be read just now.',
    seeAll: 'See all',
    openWorkspace: 'Open workspace',
    openSettings: 'Open its Settings',
    loading: 'Reading…',
  },
  ownerless: {
    heading: 'Needs a look',
    line: (n: number) =>
      n === 1
        ? '1 workspace has no active owner'
        : `${n} workspaces have no active owner`,
    why: 'Nobody can manage its members until someone is made its owner.',
    action: 'See them',
    actionLabel: 'See the workspaces without an active owner',
  },
} as const;
